/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, useEffect, useState } from "react"
import { AddImageUniversal, ImageOutlined } from "@duxton-web/icons"
import {
  Avatar,
  Grid,
  notification,
  Select,
  Tag,
  Typography,
  Upload,
  UploadFile,
} from "@duxton-web/ui"
import classNames from "classnames"

import { useTranslation, useTranslationsMap } from "common/utils/i18n"

import { AddPhotosSheet } from "ecommerce/components/AddPhotosSheet"
import {
  OptionPickerSheet,
  PickerField,
} from "ecommerce/components/OptionPickerSheet"
import {
  DECISION_ACTION,
  DecisionAction,
  REJECTION_REASON,
  RejectionReason,
  RETURN_MEDIA_MAX_PHOTOS_PER_ITEM,
  ReturnRequestItem,
  ReviewDecision,
  getDecisionActionLabel,
  getRejectionReasonHelper,
  getRejectionReasonLabel,
  getReturnReasonLabel,
} from "ecommerce/constants/returnRefund"
import {
  DRAFT_PHOTO_UID_PREFIX,
  getClarifyState,
  isDecisionComplete,
  isSupportedPhoto,
  RETURN_CLARIFY_STATE,
  stageNativePhoto,
  toRenderedPhotoList,
} from "ecommerce/utils/returnRefundHelpers"

import { isNativeBridgeAvailable } from "utils/nativeBridge"
import { isWebview } from "utils/webview"

import styles from "./ReturnReviewItemCard.modules.css"

const { Text } = Typography

/**
 * The server's `eligibleResolutions` constrains the choices when supplied; the full
 * set stands in while BE leaves the field unpopulated. (An item whose list is empty
 * never reaches this card — the review filter drops it.)
 */
const actionOptionsFor = (
  item: ReturnRequestItem,
  actionOptions: { value: DecisionAction; label: string }[],
) =>
  item.eligibleActions
    ? actionOptions.filter(option =>
        item.eligibleActions?.includes(option.value),
      )
    : actionOptions

/**
 * The draft id behind a tile, from whichever source carries it: a tile rendered
 * FROM the server holds it in its uid, while one this session uploaded holds it
 * on `response` for as long as the tile lives (Duxton stamps it there,
 * JSON-parsed — hence `String`). Undefined means nothing was staged, so there is
 * nothing to delete.
 */
const resolveRemovableAssetID = (file: UploadFile): string | undefined => {
  if (file.uid.startsWith(DRAFT_PHOTO_UID_PREFIX)) {
    return file.uid.slice(DRAFT_PHOTO_UID_PREFIX.length)
  }

  return file.response === undefined ? undefined : String(file.response)
}

interface ReturnReviewItemCardProps {
  item: ReturnRequestItem
  decision?: ReviewDecision
  /**
   * Sends an UPDATER so two writes in one tick (a removal's tombstone, then the
   * `onChange` Duxton fires right after) compose instead of clobbering.
   */
  onChange(
    itemID: string,
    /**
     * Returning undefined leaves a deleted decision deleted — required of the
     * ASYNC photo writers, which must not resurrect a decision the clarify
     * hand-off cleared while they were in flight.
     */
    update: (previous?: ReviewDecision) => ReviewDecision | undefined,
  ): void
  /**
   * Stages one photo as a server-side draft the moment it is added. Resolves with
   * the staged assetID; a rejection drops the tile (nothing was staged for it).
   */
  onUploadPhoto(
    item: ReturnRequestItem,
    file: File,
    mediaSource?: string,
  ): Promise<string>
  /**
   * Deletes one server-staged draft photo. Resolves `false` when the server kept
   * it, so the optimistically hidden tile comes back.
   */
  onRemovePhoto(item: ReturnRequestItem, assetID: string): Promise<boolean>
  /**
   * Buyer-chat hand-off, reached from the action sheet's `Initiate chat` row. The
   * HOST owns the destination (native chat callback in the app, chat-unavailable
   * modal on desktop), so the card only reports the intent — with the item,
   * because clarifying IS this item's once-per-item REQUEST_INFO decision.
   */
  onRequestChat(item: ReturnRequestItem): Promise<boolean>
}

export const ReturnReviewItemCard: FC<ReturnReviewItemCardProps> = ({
  item,
  decision,
  onChange,
  onUploadPhoto,
  onRemovePhoto,
  onRequestChat,
}) => {
  const text = useTranslationsMap({
    actionApproveReturn: "ecommerce.sellerCentre.returns.actionApproveReturn",
    actionRefundNow: "ecommerce.sellerCentre.returns.actionRefundNow",
    actionRejectRequest: "ecommerce.sellerCentre.returns.actionRejectRequest",
    actionSheetTitle: "ecommerce.sellerCentre.returns.actionSheetTitle",
    clarifyLabel: "ecommerce.sellerCentre.returns.clarifyLabel",
    goToChatCta: "ecommerce.sellerCentre.returns.goToChatCta",
    initiateChatCta: "ecommerce.sellerCentre.returns.initiateChatCta",
    photoUploadFailed: "ecommerce.sellerCentre.returns.photoUploadFailed",
    reasonContradictsDescription:
      "ecommerce.sellerCentre.returns.reasonContradictsDescription",
    reasonHelperContradictsDescription:
      "ecommerce.sellerCentre.returns.reasonHelperContradictsDescription",
    reasonHelperInconclusiveEvidence:
      "ecommerce.sellerCentre.returns.reasonHelperInconclusiveEvidence",
    reasonHelperLogisticsIssue:
      "ecommerce.sellerCentre.returns.reasonHelperLogisticsIssue",
    reasonHelperMismatchedEvidence:
      "ecommerce.sellerCentre.returns.reasonHelperMismatchedEvidence",
    reasonHelperPerfectConditionAtDispatch:
      "ecommerce.sellerCentre.returns.reasonHelperPerfectConditionAtDispatch",
    reasonInconclusiveEvidence:
      "ecommerce.sellerCentre.returns.reasonInconclusiveEvidence",
    reasonItemDamaged: "ecommerce.sellerCentre.returns.reasonItemDamaged",
    reasonLogisticsIssue: "ecommerce.sellerCentre.returns.reasonLogisticsIssue",
    reasonMismatchedEvidence:
      "ecommerce.sellerCentre.returns.reasonMismatchedEvidence",
    reasonPerfectConditionAtDispatch:
      "ecommerce.sellerCentre.returns.reasonPerfectConditionAtDispatch",
    reasonWrongItem: "ecommerce.sellerCentre.returns.reasonWrongItem",
    reviewActionLabel: "ecommerce.sellerCentre.returns.reviewActionLabel",
    reviewActionPlaceholder:
      "ecommerce.sellerCentre.returns.reviewActionPlaceholder",
    reviewAddPhotos: "ecommerce.sellerCentre.returns.reviewAddPhotos",
    reviewAddPhotosCount: "ecommerce.sellerCentre.returns.reviewAddPhotosCount",
    reviewBuyerMessage: "ecommerce.sellerCentre.returns.reviewBuyerMessage",
    reviewEvidencePhotoAlt:
      "ecommerce.sellerCentre.returns.reviewEvidencePhotoAlt",
    reviewEvidencePhotos: "ecommerce.sellerCentre.returns.reviewEvidencePhotos",
    reviewPhotoRejected: "ecommerce.sellerCentre.returns.reviewPhotoRejected",
    reviewReasonLabel: "ecommerce.sellerCentre.returns.reviewReasonLabel",
    reviewReasonPlaceholder:
      "ecommerce.sellerCentre.returns.reviewReasonPlaceholder",
    reviewReturnReason: "ecommerce.sellerCentre.returns.reviewReturnReason",
    reviewStateCompleted: "common.completed",
    reviewStatePending: "common.pending",
    reviewSupportingDocument:
      "ecommerce.sellerCentre.returns.reviewSupportingDocument",
  })
  // Single call, known values up front (this card is one item) — `useTranslation`
  // does the `<number>`/`<name>` substitution itself, PhraseApp key + tokens in.
  const reviewItemHeading = useTranslation(
    "ecommerce.sellerCentre.returns.reviewItemHeading",
    { "<number>": String(item.quantity), "<name>": item.name },
  )
  /*
   * The remaining templated entries stay as raw PhraseApp copy (literal tokens
   * still in them) — substituted inline at each call site instead of via
   * `useTranslation`, because none of them fit its one-shot, render-time-only
   * shape:
   * - `reviewEvidencePhotoAlt` is read once per photo inside a `.map`, so the
   *   argument varies per iteration — a hook can't be called that way.
   * - `reviewPhotoRejected`/`photoUploadFailed` are read inside upload
   *   callbacks, not the render body, where no hook may be called at all.
   * - `reviewAddPhotosCount`'s template repeats the SAME `<number>` token
   *   twice for two DIFFERENT values (used/max) — a single-token replacement
   *   map would stamp both slots with the same value, so this needs two
   *   sequential `.replace()` calls (each only touches the first remaining
   *   occurrence) instead. Same pattern as `VariationTable.tsx`/
   *   `ItemStockCell.tsx` elsewhere in this codebase.
   */
  const isComplete = isDecisionComplete(decision)
  const isRejecting = decision?.action === DECISION_ACTION.rejectRequest
  // One shared rule with the chat item picker — see the helper.
  const clarifyState = getClarifyState(item)

  // `useTranslationsMap` returns a new `text` object every render, so a
  // `useMemo` keyed on it would recompute every render anyway — these
  // factories are cheap lookups over a handful of enum values.
  const actionLabel = getDecisionActionLabel(text)
  const allActionOptions = Object.values(DECISION_ACTION).map(action => ({
    value: action,
    label: actionLabel[action],
  }))

  const reasonLabel = getRejectionReasonLabel(text)
  const reasonOptions = Object.values(REJECTION_REASON).map(reason => ({
    value: reason,
    label: reasonLabel[reason],
  }))

  /*
   * The sheet's variant, carrying the guidance the desktop dropdown renders through
   * `optionRender`.
   */
  const reasonHelper = getRejectionReasonHelper(text)
  const reasonSheetOptions = reasonOptions.map(option => ({
    ...option,
    helper: reasonHelper[option.value],
  }))

  /*
   * In the app the file input cannot reach the camera or gallery, so the tile opens
   * the Add-photos sheet instead and the app's own picker (`pickImage`) supplies the
   * photo. Desktop keeps the plain input, where the OS picker already covers both.
   *
   * The BRIDGE, not just the webview, is the gate: an app build that never injected
   * it would leave the sheet's rows doing nothing against a file input this mode has
   * already disabled — no photo attachable at all. Falling back to the input keeps
   * the form usable there, at the cost of the camera row.
   */
  const isNativePicker = isWebview() && isNativeBridgeAvailable()
  const [isAddPhotosOpen, setAddPhotosOpen] = useState(false)
  /*
   * Slots held by picks the app has not answered yet. They count against the cap
   * alongside the photos already on the item: a pick that is still in the app's
   * picker has changed nothing in `photos` yet, so without the reservation the add
   * tile would stay live and a second pick could land past
   * `RETURN_MEDIA_MAX_PHOTOS_PER_ITEM`.
   */
  const [pendingNativePicks, setPendingNativePicks] = useState(0)

  /*
   * Below `md` the design replaces the dropdowns with a bottom-sheet radio list.
   * Gated on WIDTH, not `isWebview()` — a narrowed desktop browser gets the sheet
   * too, and the webview reference lists the selectors as a width concern. Mirrors
   * `CategoryPickerModal`, which switches on `md === false` the same way.
   */
  const screens = Grid.useBreakpoint()
  const isNarrow = screens.md === false
  const [openSheet, setOpenSheet] = useState<"action" | "reason">()

  /*
   * The sheets only mount while narrow, so widening past `md` unmounts one without
   * clearing which was open. Narrowing again would then remount it with `open`, springing
   * a picker the merchant never asked for.
   */
  useEffect(() => {
    if (!isNarrow) {
      setOpenSheet(undefined)
    }
  }, [isNarrow])

  const actionOptions = actionOptionsFor(item, allActionOptions)

  const handleActionChange = (action: DecisionAction) =>
    /*
     * Switching away from rejection drops the REASON — nothing stale can ride
     * along with another action — but keeps the photo bookkeeping: the uploads
     * are already server-side drafts, so forgetting them here would un-block
     * Confirm mid-upload and resurrect tiles the merchant had removed.
     */
    onChange(item.id, previous =>
      action === DECISION_ACTION.rejectRequest
        ? { ...previous, action }
        : {
            action,
            supportingPhotos: previous?.supportingPhotos,
            removedDraftAssetIDs: previous?.removedDraftAssetIDs,
          },
    )

  /*
   * The rejection fields only render while `isRejecting`, so the action is already
   * `rejectRequest` at runtime; restating it is what lets TypeScript narrow the
   * optional `decision` prop into a complete `ReviewDecision`.
   */
  /*
   * Photo bookkeeping lands ASYNCHRONOUSLY — an upload settling, a refused
   * removal rolling back — by which time the merchant may have chosen another
   * action. These writes therefore patch photos onto whatever the decision is NOW;
   * only a decision that does not exist yet defaults to the one that offers
   * photos at all.
   */
  const handlePhotoChange = (patch: Partial<typeof decision>) =>
    onChange(item.id, previous =>
      // A cleared decision stays cleared: this write can land after the clarify
      // hand-off deleted it, and recreating a half-made reject here would block
      // Confirm on an item the merchant just deferred. The legitimate flows all
      // run with the reject form open, where a decision necessarily exists.
      previous === undefined ? undefined : { ...previous, ...patch },
    )

  const handleRejectionChange = (patch: Partial<ReviewDecision>) =>
    onChange(item.id, previous => ({
      ...previous,
      action: DECISION_ACTION.rejectRequest,
      ...patch,
    }))

  /*
   * One source for the tiles, the counter and the cap: server-staged drafts plus
   * this session's in-flight uploads, minus what the merchant just removed.
   */
  const photos = toRenderedPhotoList(
    item.draftPhotos,
    decision?.supportingPhotos,
    decision?.removedDraftAssetIDs,
  )
  const remainingPhotoSlots =
    RETURN_MEDIA_MAX_PHOTOS_PER_ITEM - photos.length - pendingNativePicks

  /*
   * Shared with the inspection card — the two differ only in the decision a
   * photo-first write starts from.
   */
  const handleNativePhoto = (photo: UploadFile, mediaSource: string) =>
    stageNativePhoto<ReviewDecision>({
      photo,
      mediaSource,
      text: {
        reviewPhotoRejected: (fileName: string) =>
          text.reviewPhotoRejected.replace("<name>", fileName),
        photoUploadFailed: (fileName: string) =>
          text.photoUploadFailed.replace("<name>", fileName),
      },
      defaultDecision: { action: DECISION_ACTION.rejectRequest },
      // Same cleared-stays-cleared rule as `handlePhotoChange`: the staging
      // helper's settle writes must not resurrect a decision the clarify
      // hand-off deleted while the upload was in flight.
      onChange: update =>
        onChange(item.id, previous =>
          previous === undefined ? undefined : update(previous),
        ),
      upload: (file, source) => onUploadPhoto(item, file, source),
    })

  /* One tile face, rendered by whichever element the picker mode calls for. */
  const addPhotoTileContent = (
    <>
      <AddImageUniversal className={styles.uploadIcon} />
      <Text className={styles.uploadLabel}>{text.reviewAddPhotos}</Text>
      <Text className={styles.uploadCount}>
        {text.reviewAddPhotosCount
          .replace("<number>", String(photos.length))
          .replace("<number>", String(RETURN_MEDIA_MAX_PHOTOS_PER_ITEM))}
      </Text>
    </>
  )

  return (
    <div className={styles.card}>
      <div className={styles.summary}>
        <Avatar
          shape="square"
          src={item.imageURL}
          alt={item.name}
          // Without a fallback, a missing imageURL paints an empty transparent box.
          icon={<ImageOutlined />}
          className={styles.thumbnail}
        />
        <div className={styles.summaryText}>
          <Text strong>{reviewItemHeading}</Text>
          <Text className={styles.metadata}>
            {[item.variant, item.price].filter(Boolean).join(" · ")}
          </Text>
        </div>
        <Tag
          bordered={false}
          className={classNames(
            styles.stateTag,
            isComplete ? styles.stateCompleted : styles.statePending,
          )}
        >
          {isComplete ? text.reviewStateCompleted : text.reviewStatePending}
        </Tag>
      </div>

      <div className={styles.details}>
        {item.reason && (
          <div className={styles.detailBlock}>
            <Text className={styles.metadata}>{text.reviewReturnReason}</Text>
            <Text>
              {/* Server copy first, the FE map for mock-backed items. */}
              {item.reasonLabel ??
                getReturnReasonLabel(text)[item.reason] ??
                item.reason}
            </Text>
          </div>
        )}
        {item.buyerMessage && (
          <div className={styles.detailBlock}>
            <Text className={styles.metadata}>{text.reviewBuyerMessage}</Text>
            <Text>{`“${item.buyerMessage}”`}</Text>
          </div>
        )}
        {!!item.evidencePhotos?.length && (
          <div className={styles.detailBlock}>
            <Text className={styles.metadata}>{text.reviewEvidencePhotos}</Text>
            <div className={styles.photoRow}>
              {item.evidencePhotos.map((photo, index) => (
                <img
                  key={photo}
                  className={styles.photo}
                  src={photo}
                  alt={text.reviewEvidencePhotoAlt.replace(
                    "<number>",
                    String(index + 1),
                  )}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <label className={styles.fieldLabel} htmlFor={`action-${item.id}`}>
        {text.reviewActionLabel}
        <Text type="danger" className={styles.required}>
          *
        </Text>
      </label>
      {isNarrow ? (
        <PickerField
          id={`action-${item.id}`}
          value={
            decision?.action
              ? getDecisionActionLabel(text)[decision.action]
              : undefined
          }
          placeholder={text.reviewActionPlaceholder}
          isOpen={openSheet === "action"}
          onOpen={() => setOpenSheet("action")}
        />
      ) : (
        <Select
          id={`action-${item.id}`}
          className={styles.select}
          placeholder={text.reviewActionPlaceholder}
          value={decision?.action}
          options={actionOptions}
          onChange={handleActionChange}
        />
      )}

      {isRejecting && (
        <>
          <label className={styles.fieldLabel} htmlFor={`reason-${item.id}`}>
            {text.reviewReasonLabel}
            <Text type="danger" className={styles.required}>
              *
            </Text>
          </label>
          {isNarrow ? (
            <PickerField
              id={`reason-${item.id}`}
              value={
                decision?.reason
                  ? getRejectionReasonLabel(text)[decision.reason]
                  : undefined
              }
              placeholder={text.reviewReasonPlaceholder}
              isOpen={openSheet === "reason"}
              onOpen={() => setOpenSheet("reason")}
            />
          ) : (
            <Select
              id={`reason-${item.id}`}
              className={styles.select}
              placeholder={text.reviewReasonPlaceholder}
              value={decision?.reason}
              options={reasonOptions}
              /*
               * Helper text belongs in the dropdown, where it helps the merchant choose.
               * The closed field shows the plain label alone, so `optionRender` supplies
               * the rich row rather than putting a two-line node in `label`.
               */
              optionRender={option => {
                const helper = getRejectionReasonHelper(text)[
                  option.value as RejectionReason
                ]

                if (!helper) {
                  return option.label
                }

                return (
                  <div className={styles.detailBlock}>
                    <Text strong>{option.label}</Text>
                    <Text
                      className={classNames(
                        styles.metadata,
                        styles.optionHelper,
                      )}
                    >
                      {helper}
                    </Text>
                  </div>
                )
              }}
              onChange={(reason: RejectionReason) =>
                handleRejectionChange({ reason })
              }
            />
          )}

          <div className={styles.fieldLabel}>
            {text.reviewSupportingDocument}
          </div>
          {/*
            Uploads on ADD: an accepted file goes straight through `customRequest`
            to the media-upload endpoint and the server stages it as a draft — the
            refetched detail then echoes it back in `item.draftPhotos`, which is
            what the `done` tiles render from. `LIST_IGNORE` still drops anything
            the gate rejects before a request fires.
          */}
          <Upload
            listType="picture-card"
            openFileDialogOnClick={!isNativePicker}
            multiple
            accept="image/jpeg,image/png"
            /*
             * Server drafts the merchant has not touched, then this session's own
             * tiles — which keep rendering their own entry even after the echoed
             * draft arrives, so an upload never swaps one tile for another. The
             * local `UploadFile`s hand back as-is because the list caches each
             * thumbnail by mutating its file object.
             */
            fileList={photos}
            beforeUpload={(file, batch) => {
              // Type and size first, so an unsupported file always gets told why.
              if (!isSupportedPhoto(file)) {
                notification.error({
                  message: text.reviewPhotoRejected.replace(
                    "<name>",
                    file.name,
                  ),
                })

                return Upload.LIST_IGNORE
              }

              /*
               * A multi-select can overshoot the cap: keep what fits, in pick
               * order, and drop the rest rather than letting the server reject
               * them one by one. Only SUPPORTED predecessors count — a rejected
               * file takes no slot, so counting raw batch positions would drop a
               * valid photo that had one waiting for it.
               */
              const supportedBefore = batch
                .slice(0, batch.indexOf(file))
                .filter(candidate => isSupportedPhoto(candidate)).length

              return supportedBefore < remainingPhotoSlots || Upload.LIST_IGNORE
            }}
            customRequest={({ file, onSuccess, onError }) =>
              onUploadPhoto(item, file as File).then(
                assetID => onSuccess?.(assetID),
                error => {
                  // The tile disappears via the error filter in onChange; the
                  // toast is what tells the merchant to re-add it.
                  notification.error({
                    message: text.photoUploadFailed.replace(
                      "<name>",
                      (file as File).name,
                    ),
                  })
                  onError?.(error)
                },
              )
            }
            onRemove={file => {
              /*
               * An in-flight upload cannot be cancelled: `customRequest` returns a
               * plain promise, so rc-upload's abort is a no-op and the POST still
               * stages a draft — which would come back on the next refetch with
               * nothing to hide it. Refuse the removal until the tile settles.
               */
              if (file.status === "uploading") {
                return false
              }

              const assetID = resolveRemovableAssetID(file)

              if (assetID) {
                /*
                 * Hide first, then delete: the tombstone drops the tile straight
                 * away and the REMOVE call follows. A refusal puts it back — the
                 * draft still exists server-side and would still attach.
                 */
                onChange(item.id, previous =>
                  previous === undefined
                    ? undefined
                    : {
                        ...previous,
                        removedDraftAssetIDs: [
                          ...(previous.removedDraftAssetIDs ?? []),
                          assetID,
                        ],
                      },
                )
                onRemovePhoto(item, assetID).then(removed => {
                  if (!removed) {
                    onChange(item.id, previous =>
                      previous === undefined
                        ? undefined
                        : {
                            ...previous,
                            removedDraftAssetIDs: (
                              previous.removedDraftAssetIDs ?? []
                            ).filter(id => id !== assetID),
                          },
                    )
                  }
                })
              }

              return true
            }}
            onChange={({ fileList }) =>
              handlePhotoChange({
                /*
                 * Only this session's local entries persist — server-draft tiles
                 * re-derive from `draftPhotos`, and a failed upload drops out here
                 * (its toast already asked for a re-add).
                 */
                supportingPhotos: fileList.filter(
                  file =>
                    !file.uid.startsWith(DRAFT_PHOTO_UID_PREFIX) &&
                    file.status !== "error",
                ),
              })
            }
          >
            {/* No add tile at the cap — antd hides it when there are no children. */}
            {remainingPhotoSlots > 0 &&
              (isNativePicker ? (
                /*
                 * A real BUTTON on the native path. `openFileDialogOnClick={false}`
                 * leaves rc-upload's wrapper carrying `role="button"` and a tab stop
                 * while making its key handler a no-op, so a div with an `onClick`
                 * here can be focused and never activated. The button restores
                 * Enter/Space, and `.uploadTile` neutralises its chrome so it renders
                 * exactly as the browser path's tile does.
                 */
                <button
                  type="button"
                  className={styles.uploadTile}
                  onClick={() => setAddPhotosOpen(true)}
                >
                  {addPhotoTileContent}
                </button>
              ) : (
                <div className={styles.uploadTile}>{addPhotoTileContent}</div>
              ))}
          </Upload>
          {isNativePicker && (
            <AddPhotosSheet
              open={isAddPhotosOpen}
              onClose={() => setAddPhotosOpen(false)}
              onPhoto={handleNativePhoto}
              onPickingChange={isPicking =>
                setPendingNativePicks(count => count + (isPicking ? 1 : -1))
              }
            />
          )}
        </>
      )}

      {isNarrow && (
        <>
          <OptionPickerSheet
            open={openSheet === "action"}
            title={text.actionSheetTitle}
            options={actionOptions}
            value={decision?.action}
            /*
             * Present but never selectable: buyer chat is a hand-off, not an
             * outcome, so it sits outside the radio group. `getClarifyState`
             * is the one rule for its states, shared with the chat item
             * picker: CHAT jumps to the room (the host reads `item.roomID` and
             * skips the submit), AVAILABLE is the live clarify entry, SPENT is
             * greyed with nowhere to go, HIDDEN renders nothing.
             */
            secondaryAction={
              clarifyState !== RETURN_CLARIFY_STATE.hidden
                ? {
                    label: text.clarifyLabel,
                    ctaLabel:
                      clarifyState === RETURN_CLARIFY_STATE.chat
                        ? text.goToChatCta
                        : text.initiateChatCta,
                    // Once per item, enforced server-side.
                    disabled: clarifyState === RETURN_CLARIFY_STATE.spent,
                    // Closes first: the submit shows the global loader, and the chat
                    // destination must not render under an open sheet.
                    onPress: () => {
                      setOpenSheet(undefined)
                      onRequestChat(item)
                    },
                  }
                : undefined
            }
            onCancel={() => setOpenSheet(undefined)}
            onSelect={value => {
              handleActionChange(value as DecisionAction)
              setOpenSheet(undefined)
            }}
          />
          <OptionPickerSheet
            open={openSheet === "reason"}
            title={text.reviewReasonLabel}
            options={reasonSheetOptions}
            value={decision?.reason}
            onCancel={() => setOpenSheet(undefined)}
            onSelect={value => {
              handleRejectionChange({ reason: value as RejectionReason })
              setOpenSheet(undefined)
            }}
          />
        </>
      )}
    </div>
  )
}
